import React from 'react';
import { connect } from 'react-redux';
import { round } from 'lodash';
import { Row, Col, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import {
  faImage,
  faMobile,
  faShareAlt,
  faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { faUsersViewfinder } from '@fortawesome/pro-solid-svg-icons';

import Loader from '../../../shared/Loader';
import Info from '../../../components/Info';
import env from '../../../config/env';
import GroupMemoryChart from './GroupMemoryChart';
import ShareChart from './ShareChart';
import SectionChart from './HistogramChart';
import SourceChart from './SourceChart';
import OverlayChart from './OverlayChart';
import OverviewContainer from './OverviewContainer';
import CameraUsageChart from './CameraUsageChart';

import style from './style.module.scss';

const numberFormat = Intl.NumberFormat();

const BasicSection = ({
  basicData,
  pastEventData,
  error,
  isFetching,
}) => {
  if (isFetching) {
    return <Loader />;
  }

  if (error) {
    return (
      <div>
        Error querying Basic Data for the selected event
      </div>
    );
  }

  if (!basicData || !pastEventData) return null;

  const {
    overlayUsage = [],
    inboundSources = [],
    engagedUsersCount = 0,
    engagedUsersMean = 4,
    engagedUsersMedian = 4,
    devicesCount = 0,
    devicesUnique = 0,
    devicesSessionPerUser = 0,
    photosCount = 0,
    sharesCount = 0,
    sharesUserShareRate = 0,
    sharesPhotoShareRate = 0,
  } = basicData;
  const prevDevices = pastEventData.devicesCount ?? 0;
  const prevPhotos = pastEventData.photosCount ?? 0;
  const prevShares = pastEventData.sharesCount ?? 0;

  const isOverlayFullRow = overlayUsage.length > 3;
  const shouldShowSourcesChart = inboundSources.length > 1;
  const isAiApp = env.USER_APP_TYPE === 'ai';

  return (
    <div>
      <h4>Overview</h4>
      <Row>
        <Col xs="12" sm="6" md="6" lg="3" className="mb-2">
          <OverviewContainer
            title="Engaged Users"
            description="This represents the total engaged users, calculated by multiplying the number of devices by the average group size. Our AI analyzes images and clips to detect faces and estimate group sizes accurately. Faces obscured by overlays are excluded from this calculation."
            value={engagedUsersCount}
            previousValue={pastEventData.engagedUsersCount ?? 0}
            icon={faUsersViewfinder}
            variant="green"
            cardPlacement="right"
          >
            <Card className={classnames('p-3', 'ms-auto', style.engagedUsersCard)}>
              <h5 className="mb-1">Engaged Users Per Photo:</h5>
              <div className="d-flex mt-2 align-items-center">
                <div className={style.cardIcon}>
                  <FontAwesomeIcon icon={faUser} />
                </div>
                <div className="ml-2">
                  <h3 className="mb-1">{engagedUsersMedian || 4}</h3>
                  <h6 className={classnames('mb-0', style.rowDesc)}>Median</h6>
                </div>
              </div>
              <div className="d-flex mt-3 align-items-center">
                <div className={style.cardIcon}>
                  <FontAwesomeIcon icon={faUser} />
                </div>
                <div className="ml-2">
                  <h3 className="mb-1">{round(engagedUsersMean || 4, 2)}</h3>
                  <h6 className={classnames('mb-0', style.rowDesc)}>Mean</h6>
                </div>
              </div>
            </Card>
          </OverviewContainer>
        </Col>
        <Col xs="12" sm="6" md="6" lg="3" className="mb-2">
          <OverviewContainer
            title="Devices"
            description={(
              <>
                This is the number of devices that have used the app to take their snapshot.
              </>
            )}
            value={devicesCount}
            previousValue={prevDevices}
            icon={faMobile}
            variant="yellow"
            cardPlacement="right"
          >
            <Card className={classnames('p-3', style.shareRateCard, style.deviceCard)}>
              <h5 className="mb-1">Number of Unique Devices:</h5>
              <div className="d-flex align-items-center">
                <div className={style.cardIcon}>
                  <FontAwesomeIcon icon={faMobile} />
                </div>
                <div className="ml-2">
                  <h3 className="mb-1">{numberFormat.format(devicesUnique)}</h3>
                  <h6 className={classnames('mb-0', style.rowDesc)}>Unique Devices</h6>
                </div>
              </div>
              <div className="d-flex mt-3 align-items-center">
                <div className={style.cardIcon}>
                  <FontAwesomeIcon icon={faUser} />
                </div>
                <div className="ml-2">
                  <h3 className="mb-1">{Number(devicesSessionPerUser).toFixed(2)}</h3>
                  <h6 className={classnames('mb-0', style.rowDesc)}>Sessions per user</h6>
                </div>
              </div>
            </Card>
          </OverviewContainer>
        </Col>
        <Col xs="12" sm="6" md="6" lg="3" className="mb-2">
          <OverviewContainer
            title="Photos"
            description="This is the number of total photos taken. E.g. If 10 devices take 20 photos total, this would count as 20 photos"
            value={photosCount}
            previousValue={prevPhotos}
            icon={faImage}
            variant="purple"
          />
        </Col>
        <Col xs="12" sm="6" md="6" lg="3">
          <OverviewContainer
            title="Shares"
            description={(
              <>
                This is the number of share actions taken by photographers. E.g. If 10
                photographers take 20 photos and 5 were shared, this would count as 5 shares.
              </>
            )}
            value={sharesCount}
            icon={faShareAlt}
            variant="red"
            previousValue={prevShares}
          >
            <Card className={classnames('p-3', style.shareRateCard)}>
              <h5 className="mb-1">Share Details</h5>
              <h6 className={classnames('mb-0', style.cardDesc)}>
                Photos being shared by your audiences to their personal networks.
              </h6>
              <div className="d-flex mt-2 align-items-center">
                <div className={style.cardIcon}>
                  <FontAwesomeIcon icon={faUser} />
                </div>
                <div className="ml-2">
                  <h3 className="mb-1">
                    {numberFormat.format(sharesUserShareRate)}
                    %
                    <Info content="The percentage of users who shared. E.g. If 10 users took photos, and 5 users shared one or more photos, then the user share rate would be 50%" className="ml-2 align-baseline" />
                  </h3>
                  <h6 className={classnames('mb-0', style.rowDesc)}>User Share Rate</h6>
                </div>
              </div>
              <div className="d-flex mt-3 align-items-center">
                <div className={style.cardIcon}>
                  <FontAwesomeIcon icon={faImage} />
                </div>
                <div className="ml-2">
                  <h3 className="mb-1">
                    {numberFormat.format(sharesPhotoShareRate)}
                    %
                    <Info content="The percentage of photos shared. E.g. If 10 users took 20 photos, and 5 photos were shared in total, then the photo share rate would be 25%." className="ml-2 align-baseline" />
                  </h3>
                  <h6 className={classnames('mb-0', style.rowDesc)}>Photo Share Rate</h6>
                </div>
              </div>
            </Card>
          </OverviewContainer>
        </Col>
      </Row>
      <div className="mt-4">
        <Row className="mt-4">
          <Col>
            <GroupMemoryChart />
          </Col>
        </Row>
        <Row>
          <Col md={isOverlayFullRow ? 12 : 8} className="mt-4">
            <OverlayChart />
          </Col>
          <Col md={isOverlayFullRow ? 12 : 4} className="mt-4">
            <ShareChart />
          </Col>
        </Row>
        <Row>
          <Col md={isAiApp ? 12 : 4} className="mt-4">
            <SourceChart />
          </Col>
          {!isAiApp && (
            <Col
              md={shouldShowSourcesChart ? 8 : 12}
              className="mt-4"
            >
              <SectionChart />
            </Col>
          )}
        </Row>
        {!isAiApp && (
          <Row>
            <Col md={12} className="mt-4">
              <CameraUsageChart />
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};

export default connect(
  ({
    analytics: {
      basic: { isFetching, data: basicData, error },
      pastEvent: { data: pastEventData },
    },
  }) => ({
    isFetching,
    basicData,
    error,
    pastEventData,
  }),
)(BasicSection);
